import {Component, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {FormControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {AlertService} from '../../../sdk/service/alert.service';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {BusinessNature, PublicService} from 'web-angular-client';
import {AsyncValidators} from '../../../sdk/validator/AsyncValidators';
import {SyncValidators} from '../../../sdk/validator/SyncValidators';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {SignupStep1BaseComponent} from './signup-step1-base.component';

@Component({
    selector: 'app-signup-step1-trimmed',
    templateUrl: './signup-step1-trimmed.component.html',
    styleUrls: ['./signup-step1.shared.scss'],
    standalone: false
})
export class SignupStep1TrimmedComponent extends SignupStep1BaseComponent
{
    protected natureOfBusinessList: BusinessNature[] = [];

    constructor(
        router: Router,
        formBuilder: UntypedFormBuilder,
        alertService: AlertService,
        authenticationService: AuthenticationService,
        publicService: PublicService,
        environmentService: EnvironmentService,
        destroyRef: DestroyRef,
    )
    {
        super(router, formBuilder, alertService, authenticationService, publicService, environmentService, destroyRef);
    }

    /**
     * eCommerce merchants sign up to their own service terms; until a brand configures them, the standard terms apply.
     */
    override get serviceTermsLink(): string
    {
        return this.environmentService.gatewayMerchantTermsLink || super.serviceTermsLink;
    }

    /**
     * Names the eCommerce service terms only when the brand configures them, matching {@link serviceTermsLink}.
     */
    override get serviceTermsLabel(): string
    {
        return this.environmentService.gatewayMerchantTermsLink
            ? this.environmentService.gatewayMerchantTermsLabel
            : super.serviceTermsLabel;
    }

    protected buildForm(): UntypedFormGroup
    {
        return this.formBuilder.group({
            businessType: new FormControl(
                {value: this.businessType, disabled: this.shouldDisableBusinessType()},
                Validators.required),
            businessDisplayName: ['', [Validators.maxLength(30)]],
            businessName: [
                '',
                [Validators.required, Validators.maxLength(255)],
                AsyncValidators.validateBusinessName(this.publicService)
            ],
            name: ['', Validators.required],
            surname: ['', Validators.required],
            email: [
                '', [Validators.required, Validators.email], AsyncValidators.validateUserEmail(this.publicService)
            ],
            confirmEmail: ['', [Validators.required]],
            mobileNumber: ['', [Validators.required, SyncValidators.validateMobileNumber()]],
            password: ['', [Validators.required, SyncValidators.validatePassword]],
            confirmPassword: ['', Validators.required],
            clientBase: [null, []],
            averageTransactionValue: [null, []],
            marketingConsent: [false],
            natureOfBusiness: [null, Validators.required],
            termsAccepted: [false, Validators.requiredTrue]
        }, {validator: [SyncValidators.validatePasswordMatch(), SyncValidators.validateEmailMatch()]});
    }

    protected afterFormBuilt(): void
    {
        this.loadNatureOfBusinessList();
    }

    protected getNatureOfBusiness(): string | undefined
    {
        return this.getSignUpFormControl('natureOfBusiness').value;
    }

    private loadNatureOfBusinessList(): void
    {
        this.publicService.getNatureOfBusinessList()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (list: BusinessNature[]) =>
                {
                    this.natureOfBusinessList = list;
                },
                error: () =>
                {
                    this.alertService.error('Could not load nature of business list.');
                }
            });
    }
}
